import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	MatDialog,
	MatDialogRef,
	MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AddressConstants } from '../../../../modules/address/address.constants';
import { UsersService } from '../../../../modules/users/users.service';
import { IdAndMessageResponse } from '../../../../shared/models/id-message-response.model';
import { DataTypeUtils } from '../../../../utils/data-type.util';
import { normalizeEmptyToNull } from '../../../../utils/fields.utils';
import { PhoneUtils } from '../../../../utils/phone-utils';
import { NotificationsService } from '../../../notifications/notifications.service';
import { ProductsConfigHelper } from '../../../products/products.config';
import { UserDataModel } from '../../models/beneficiaries.model';
import {
	GroupsListSummary,
	OrgListSummary,
} from '../../models/organizations.model';
import { ProductsSummary } from '../../models/products.model';
import { OrganizationsLegacyService } from '../../organizations-legacy.service';
import {
	fetchCpfStatusClear,
	fetchCpfStatusStart,
	fetchEmailAvailabilityStart,
	fetchOrgGroupsStart,
	fetchOrgsSummaryListStart,
	restoreFormReset,
	fetchOrgRegStart,
} from '../../state/organizations.actions';
import {
	selectCpfBeneficiario,
	selectEmailAvailability,
	selectGroupsList,
	selectOrgList,
	selectUserData,
	selectOrgRegistration,
} from '../../state/organizations.selector';
import { OrgGroupModalComponent } from '../user-registration/org-group-modal/org-group-modal.component';
import { OrgListModalComponent } from '../user-registration/org-list-modal/org-list-modal.component';

@Component({
	selector: 'app-cpf-alert-dialog',
	template: `
		<app-dialog
			modalTitle="Atenção"
			titleButton="Sim"
			secTitleButton="Não"
			[secButton]="true"
			[asAlert]="true"
			(dialogClose)="dialogRef.close(false)"
			(dialogSave)="dialogRef.close(true)"
		>
			<p class="lh-md">{{ data }}</p>
		</app-dialog>
	`,
})
export class CpfAlertDialogComponent {
	constructor(
		public dialogRef: MatDialogRef<CpfAlertDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: string,
	) {}
}

@Component({
	selector: 'app-beneficiary-creation',
	styleUrls: ['./beneficiary-creation.component.scss'],
	templateUrl: './beneficiary-creation.component.html',
})
export class BeneficiaryCreationComponent implements OnInit, OnDestroy {
	alertBody: string[] = [
		'Já existe um usuário cadastrado com esse CPF, deseja cadastrá-lo como beneficiário?',
		'Há um dependente cadastrado com esse CPF. Você deseja desvinculá-lo e cadastrá-lo como um titular?',
	];
	alertRef!: MatDialogRef<CpfAlertDialogComponent>;

	auxStateForm: boolean = true;
	auxStateForm2: boolean = true;
	beneficiaryGroup!: { id: string; name: string };
	beneficiaryId: string = '';
	cepPattern = '[0-9]{8}';
	cities: string[] = [];
	connectedGroupIds: string[] = [];
	connectedOrgIds: string[] = [];

	cpfPattern = '[0-9]{11}';
	cpfStatus: number | null = null;

	cpfStep = 0;
	cpfUserData: UserDataModel | null = null;
	dialogRef!: MatDialogRef<OrgGroupModalComponent | OrgListModalComponent>;
	enableResetPass = true;
	groups: GroupsListSummary[] = [];
	groupsIds: string[] = [];
	groupsIdsDis: string[] = [];
	groupsInput: string = '';
	initialGroupsId: string[] = [];
	initialOrganizationInput: string = '';
	initialOrganizationsIds: string[] = [];
	inputId: string = String(Math.random());
	isDisabledGroupModal = false;

	isDisabledOrganizationModal = true;
	isDisabledSaveButton = false;
	isLoading: boolean = true;
	isRegistering: boolean = false;
	organizationId: string = '';

	organizationInput: string = '';
	organizations: OrgListSummary[] = [];
	organizationsIds: string[] = [];
	orgsIdsDis: string[] = [];
	phoneMask = (value: string): string => {
		this.userRegistrationForm.get('phone')?.updateValueAndValidity();

		return value ? PhoneUtils.phoneMask(value) : '';
	};
	products: ProductsSummary[] = [];
	uf = AddressConstants.ufLabels;
	userId: string | null = null;

	userRegistrationForm: UntypedFormGroup;

	private onSubmitSubscription$: Subscription = new Subscription();
	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly dialog: MatDialog,
		private readonly fb: UntypedFormBuilder,
		private readonly notifications: NotificationsService,
		private readonly organizationsService: OrganizationsLegacyService,
		private readonly productsConfigHelper: ProductsConfigHelper,
		private readonly router: Router,
		private readonly store: Store,
		private readonly usersService: UsersService,
	) {
		this.userRegistrationForm = this.fb.group({
			cpf: [null, [Validators.required, Validators.pattern(this.cpfPattern)]],
			email: [null],
			name: [null, [Validators.required]],
			phone: [
				null,
				[Validators.required, Validators.pattern(DataTypeUtils.phone.pattern)],
			],
		});
	}

	checkOrganizationProducts(id: string): void {
		this.store.dispatch(fetchOrgRegStart({ id }));
		this.store
			.select(selectOrgRegistration)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((o) => {
				if (o?.products) {
					this.products = o.products;
					const requiredAttributesOwner = this.getRequiredAttributesOwner(
						this.products,
					);

					BeneficiaryCreationComponent.setupValidators(
						this.userRegistrationForm,
						requiredAttributesOwner,
					);
				}
			});
	}

	createUser(request: any): void {
		this.onSubmitSubscription$ = this.usersService
			.createUser({
				dto: request,
				organizationId: this.organizationId,
			})
			.subscribe(
				async (response: IdAndMessageResponse) => {
					this.isLoading = false;
					this.beneficiaryId = response?.id;
					this.notifications.updateBellCount();
					await this.router.navigate([
						'organizacoes',
						this.organizationId,
						'beneficiarios',
						'cadastro',
						this.beneficiaryId,
					]);
				},
				(error) => {
					this.isLoading = false;
					this.enableFields();
					this.organizationsService.showSnack(error.error?.message, 'error');
				},
			);
	}

	disableFields(): void {
		this.userRegistrationForm.get('cpf')!.disable();
		this.userRegistrationForm.get('name')!.disable();
		this.userRegistrationForm.get('phone')!.disable();
		this.userRegistrationForm.get('email')!.disable();
	}

	disabledSaveButton(): boolean {
		if (
			this.userRegistrationForm.invalid ||
			!this.organizationsIds.length ||
			!this.groupsIds.length
		) {
			return true;
		}

		return (
			this.beneficiaryId.length > 0 &&
			this.isDisabledSaveButton &&
			this.userRegistrationForm.pristine
		);
	}

	editUser(request: any): void {
		this.onSubmitSubscription$ = this.usersService
			.editUser({
				dto: request,
				organizationId: this.organizationId,
				userId: this.userId!,
			})
			.subscribe(
				async (response: IdAndMessageResponse) => {
					this.isLoading = false;
					this.beneficiaryId = response?.id;
					this.organizationsService.showSnack(
						response?.message,
						response?.type,
					);
				},
				(error) => {
					this.isLoading = false;
					this.organizationsService.showSnack(error.error?.message, 'error');
					this.enableFields();
				},
			);
	}

	enableFields(): void {
		this.userRegistrationForm.get('cpf')!.enable();
		this.userRegistrationForm.get('name')!.enable();
		this.userRegistrationForm.get('phone')!.enable();
		this.userRegistrationForm.get('email')!.enable();
	}

	getRequiredAttributesOwner(products: ProductsSummary[]): string[] {
		const ownerAttributes: string[] = [];
		products.forEach((product) => {
			if (product.id) {
				const productConfig = this.productsConfigHelper.findById(product.id);

				if (productConfig?.requiredAttributesOwner) {
					ownerAttributes.push(...productConfig.requiredAttributesOwner);
				}
			}
		});

		return ownerAttributes;
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.onSubmitSubscription$.unsubscribe();
		this.store.dispatch(fetchCpfStatusClear());
		this.store.dispatch(restoreFormReset());
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id }) => {
			this.organizationId = id;
			this.store
				.select(selectGroupsList)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((groupsList) => {
					if (groupsList.length) {
						this.groups = groupsList;
						this.initialGroupsId = groupsList.map((group) => group.id);
						this.beneficiaryGroup = groupsList.find(
							(group) => group.name === 'beneficiario',
						)!;
						this.groupsIds = [this.beneficiaryGroup.id];
						this.groupsInput = this.processLabelCase(
							this.beneficiaryGroup.name,
						);
					}
				});
			this.store
				.select(selectOrgList)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((organizationsList) => {
					this.processOrganizationList(id, organizationsList);
				});
			this.isLoading = false;
			this.store.dispatch(fetchOrgsSummaryListStart({ id }));
			this.store.dispatch(fetchOrgGroupsStart({ id }));
			this.checkOrganizationProducts(id);
		});
	}

	onCpfBlur(): void {
		if (this.userRegistrationForm.get('cpf')!.valid) {
			const cpf: string = this.userRegistrationForm.get('cpf')!.value;
			this.store.dispatch(
				fetchCpfStatusStart({ cpf, orgId: this.organizationId }),
			);
			if (this.cpfStep <= 0) {
				this.cpfStep = 1;
				this.store
					.select(selectCpfBeneficiario)
					.pipe(takeUntil(this.unsubscribe$))
					.subscribe((result) => {
						if (result) {
							this.cpfStatus = result;
							this.cpfStep = 1;
						}
					});
				this.store
					.select(selectUserData)
					.pipe(takeUntil(this.unsubscribe$))
					.subscribe((data) => {
						if (data) {
							if (data.cpf === null) {
								this.userId = null;
								this.cpfUserData = null;
							} else {
								this.cpfUserData = data;
							}
							this.onCpfChecked();
						}
					});
			}
		}
	}

	onCpfChecked(): void {
		switch (this.cpfStatus) {
			case 1: // Criação de Beneficiário Titular + Relacionamento com Usuário existente = Dialog:0
				this.alertRef = this.dialog.open(CpfAlertDialogComponent, {
					data: this.alertBody[0],
					disableClose: true,
					maxWidth: undefined,
				});
				this.alertRef.afterClosed().subscribe((result) => {
					if (result) {
						this.userId = this.cpfUserData!.id!;
						this.userRegistrationForm = this.fb.group({
							cpf: [
								{ disabled: true, value: this.cpfUserData!.cpf },
								[Validators.required, Validators.pattern(this.cpfPattern)],
							],
							email: [
								{ disabled: true, value: this.cpfUserData!.email },
								[Validators.required, Validators.email],
							],
							name: [
								{ disabled: false, value: this.cpfUserData!.name },
								[Validators.required],
							],
							phone: [
								{ disabled: false, value: this.cpfUserData!.phone },
								[
									Validators.required,
									Validators.pattern(DataTypeUtils.phone.pattern),
								],
							],
						});
						const isOper =
							this.cpfUserData!.groups_id.filter((g) => g.name === 'operador')
								.length > 0;
						this.connectedGroupIds = this.cpfUserData!.groups_id.map(
							(g) => g.id,
						);
						this.groupsIds = this.cpfUserData!.groups_id.map((g) => g.id);
						if (!this.groupsIds.includes(this.beneficiaryGroup.id)) {
							this.groupsIds.push(this.beneficiaryGroup.id);
						}
						this.processUserData(
							this.organizationId,
							this.cpfUserData!,
							isOper,
						);
						this.isDisabledOrganizationModal = isOper;
					} else {
						this.userRegistrationForm.get('cpf')!.setValue(null);
					}
				});
				break;
			case 2: // Criação de Beneficiário Titular + Exclusão de Beneficiário Dependente = Dialog:1
				this.alertRef = this.dialog.open(CpfAlertDialogComponent, {
					data: this.alertBody[1],
					disableClose: true,
					maxWidth: undefined,
				});
				this.alertRef.afterClosed().subscribe((result) => {
					if (result) {
						this.isRegistering = true;
						this.userId = this.cpfUserData!.id!;
						this.userRegistrationForm = this.fb.group({
							cpf: [
								{ disabled: true, value: this.cpfUserData!.cpf },
								[Validators.required, Validators.pattern(this.cpfPattern)],
							],
							email: [
								{ disabled: true, value: this.cpfUserData!.email },
								[Validators.required, Validators.email],
							],
							name: [
								{ disabled: false, value: this.cpfUserData!.name },
								[Validators.required],
							],
							phone: [
								{ disabled: false, value: this.cpfUserData!.phone },
								[
									Validators.required,
									Validators.pattern(DataTypeUtils.phone.pattern),
								],
							],
						});
						const isOper =
							this.cpfUserData!.groups_id.filter((g) => g.name === 'operador')
								.length > 0;
						this.connectedGroupIds = this.cpfUserData!.groups_id.map(
							(g) => g.id,
						);
						this.groupsIds = this.cpfUserData!.groups_id.map((g) => g.id);
						if (!this.groupsIds.includes(this.beneficiaryGroup.id)) {
							this.groupsIds.push(this.beneficiaryGroup.id);
						}
						this.processUserData(
							this.organizationId,
							this.cpfUserData!,
							isOper,
						);
						this.isDisabledOrganizationModal = isOper;
						this.onEmailBlur();
						// this.isDisabledGroupModal = true;
					} else {
						this.userRegistrationForm.get('cpf')!.setValue(null);
					}
				});
				break;
			case 3: // CPF já cadastrado
				this.userRegistrationForm.get('cpf')!.setErrors({ minlength: true });
				break;
			case 4: // CPF não encontrado
				this.userRegistrationForm.get('cpf')!.updateValueAndValidity();
				this.userRegistrationForm.get('name')!.enable();
				this.userRegistrationForm.get('phone')!.enable();
				this.userRegistrationForm.get('email')!.enable();
				break;
			default:
		}
	}

	onEmailBlur(): void {
		const email = this.userRegistrationForm.get('email');

		if (email?.value && email!.valid) {
			this.store.dispatch(
				fetchEmailAvailabilityStart({
					email: this.userRegistrationForm.get('email')!.value,
				}),
			);
			this.store
				.select(selectEmailAvailability)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (result) {
						this.userRegistrationForm.get('email')!.updateValueAndValidity();
					} else {
						this.userRegistrationForm
							.get('email')!
							.setErrors({ minlength: true });
					}
				});
		}
	}

	onSubmit(): void {
		this.isLoading = true;

		const request = this.processForm();

		if (request) {
			if (this.beneficiaryId && this.organizationId) {
				this.router.navigate([
					'organizacoes',
					this.organizationId,
					'beneficiarios',
					'cadastro',
					this.beneficiaryId,
				]);
			}

			if (this.userId && !this.isRegistering) {
				this.editUser(request);
			} else {
				this.createUser(request);
			}
		}
	}

	openOrganizationsGroupsModal(): void {
		this.dialogRef = this.dialog.open(OrgGroupModalComponent, {
			data: {
				disabled: this.beneficiaryGroup.id,
				groups: this.groups,
				selected: this.groupsIds,
			},
			maxWidth: undefined,
		});
		this.dialogRef.afterClosed().subscribe((result: GroupsListSummary[]) => {
			if (result) {
				const ids = result.map((p: GroupsListSummary) => p.id);
				const idsDis = this.connectedGroupIds.filter((ig) => !ids.includes(ig));
				const names = result.map((p: GroupsListSummary) =>
					this.processLabelCase(p.name),
				);

				if (!names.includes('Operador')) {
					this.orgsIdsDis = this.connectedOrgIds.filter(
						(id) => id !== this.initialOrganizationsIds[0],
					);
					this.organizationsIds = this.initialOrganizationsIds;
					this.organizationInput = this.initialOrganizationInput;
					this.isDisabledOrganizationModal = true;
				} else {
					this.isDisabledOrganizationModal = false;
				}
				this.groupsIds = ids;
				this.groupsIdsDis = idsDis;
				this.groupsInput = names.toString().replace(',', ', ');
				this.isDisabledSaveButton = false;
			}
		});
	}

	openOrganizationsListModal(): void {
		if (!this.isDisabledOrganizationModal) {
			this.dialogRef = this.dialog.open(OrgListModalComponent, {
				data: {
					disabled: this.organizationId,
					orgs: this.organizations,
					selected: this.organizationsIds,
				},
				maxWidth: undefined,
			});
			this.dialogRef.afterClosed().subscribe((result: OrgListSummary[]) => {
				if (result) {
					const ids = result.map((p: OrgListSummary) => p.id);
					const idsDis = this.connectedOrgIds.filter((ig) => !ids.includes(ig));
					const names = result.map((p: OrgListSummary) => p.name);
					this.organizationsIds = ids;
					this.orgsIdsDis = idsDis;
					this.organizationInput = names.toString().replace(',', ', ');
					this.isDisabledSaveButton = false;
				}
			});
		}
	}

	processForm(): any {
		this.enableFields();

		if (this.userRegistrationForm.invalid) {
			return null;
		}

		const reg = this.userRegistrationForm.value;
		reg.email = normalizeEmptyToNull(reg.email);
		reg.phone = normalizeEmptyToNull(reg.phone);

		const request: any = {
			id: this.userId,
			active: true,
			status: this.cpfStatus,
			...reg,
			groups_id: this.groupsIds,
			orgs_id: this.organizationsIds,
		};

		if (this.cpfStatus === 1) {
			request.orgs_disconnect = this.orgsIdsDis;
			request.groups_disconnect = this.groupsIdsDis;
		}

		this.disableFields();

		return request;
	}

	processLabelCase(label: string) {
		return label.replace(/\w\S*/g, (text: string) => {
			return text.charAt(0).toUpperCase() + text.substring(1).toLowerCase();
		});
	}

	processOrganizationList(id: string, ol: OrgListSummary[]): void {
		this.organizations = ol;
		for (const org of ol) {
			if (org.id === id) {
				this.organizationsIds = [org.id];
				this.organizationInput = org.name;
				this.initialOrganizationsIds = [org.id];
				this.initialOrganizationInput = org.name;
			} else {
				for (const orgS of org.secondary) {
					if (orgS.id === id) {
						this.organizationsIds = [orgS.id];
						this.organizationInput = orgS.name;
						this.initialOrganizationsIds = [orgS.id];
						this.initialOrganizationInput = orgS.name;
					}
				}
			}
		}
	}

	processUserData(id: string, user: UserDataModel, isOper: boolean): void {
		let addInputSuffix = false;

		if (isOper) {
			this.connectedOrgIds = user.orgs_id.map(
				(organization) => organization.id,
			);
			this.initialGroupsId = user.groups_id.map((group) => group.id);
			this.groupsIds = user.groups_id.map((group) => group.id);
			if (!this.groupsIds.includes(this.beneficiaryGroup.id)) {
				this.groupsIds.push(this.beneficiaryGroup.id);
				addInputSuffix = true;
			}
			this.groupsInput =
				user.groups_id
					.map((group) => this.processLabelCase(group.name))
					.toString()
					.replace(',', ', ') + (addInputSuffix ? ', Beneficiário' : '');
			this.initialOrganizationsIds = [id];
			this.organizationsIds = user.orgs_id.map(
				(organization) => organization.id,
			);
			let organizationLabel = '';

			for (const organization of this.organizations) {
				if (organization.id === id) {
					organizationLabel = organization.name;
					break;
				}

				for (const sub of organization.secondary) {
					if (sub.id === id) {
						organizationLabel = sub.name;
						break;
					}
				}
			}
			this.initialOrganizationInput = organizationLabel;
			this.organizationInput = user.orgs_id
				.map((o) => o.name)
				.toString()
				.replace(',', ', ');
		} else {
			this.connectedOrgIds = [id];
			this.initialGroupsId = user.groups_id.map((group) => group.id);
			this.groupsIds = user.groups_id.map((group) => group.id);
			if (!this.groupsIds.includes(this.beneficiaryGroup.id)) {
				this.groupsIds.push(this.beneficiaryGroup.id);
				addInputSuffix = true;
			}
			this.groupsInput =
				user.groups_id
					.map((group) => this.processLabelCase(group.name))
					.toString()
					.replace(',', ', ') + (addInputSuffix ? ', Beneficiário' : '');
			this.initialOrganizationsIds = [id];
			this.organizationsIds = this.initialOrganizationsIds;
			let organizationLabel = '';

			for (const org of this.organizations) {
				if (org.id === id) {
					organizationLabel = org.name;
					break;
				}

				for (const sub of org.secondary) {
					if (sub.id === id) {
						organizationLabel = sub.name;
						break;
					}
				}
			}
			this.initialOrganizationInput = organizationLabel;
			this.organizationInput = this.initialOrganizationInput;
		}
	}

	updateValidation(): void {
		for (const control in this.userRegistrationForm.controls) {
			this.userRegistrationForm.get(control)!.markAsTouched();
			this.userRegistrationForm
				.get(control)!
				.updateValueAndValidity({ onlySelf: true });
		}
	}

	private normalizeEmptyToNull(value: string | null): string | null {
		return value && value.trim() !== '' ? value : null;
	}

	static setupValidators(
		form: UntypedFormGroup,
		requiredAttributes: string[],
	): void {
		for (const controlName in form.controls) {
			form.get(controlName)?.clearValidators();
		}

		for (const requiredAttribute of requiredAttributes) {
			if (form.controls[requiredAttribute]) {
				form.get(requiredAttribute)?.setValidators(Validators.required);
			}
		}

		form.get('email')?.setValidators([Validators.email]);

		for (const controlName in form.controls) {
			form.get(controlName)?.updateValueAndValidity();
		}
	}
}
